import { useEffect, useMemo, useState } from 'react'
import type { AwardBodySummary, AwardsIndex, RawEdition } from './awards'
import { editionsForBody } from './awards'
import { CeremonyTimeline } from './CeremonyTimeline'
import { CategoryList } from './CategoryList'

interface Props {
  index: AwardsIndex
  body: AwardBodySummary
  onBack: () => void
  onOpenDetail: (tmdbId: number, type: 'movie' | 'tv') => void
  /**
   * Optional edition id to pre-select. When provided (via the
   * compact-awards deep-link from a detail modal) the effect below
   * picks that edition instead of the default `editions[0]`.
   */
  initialEditionId?: string | null
}

/**
 * The "body detail" page: sidebar of ceremonies on the left, header +
 * scrollable category content on the right. Matches
 * `AwardDetailView.tsx` on the desktop.
 */
export function BodyDetail({
  index,
  body,
  onBack,
  onOpenDetail,
  initialEditionId,
}: Props) {
  const editions = useMemo(
    () => editionsForBody(index, body.wikidataId),
    [index, body.wikidataId]
  )

  const [selected, setSelected] = useState<RawEdition | null>(() => {
    if (initialEditionId) {
      const match = editions.find((e) => e.id === initialEditionId)
      if (match) return match
    }
    return editions[0] ?? null
  })

  // When the body or the requested deep-link edition changes, pick the
  // matching ceremony — or fall back to the newest.
  useEffect(() => {
    if (initialEditionId) {
      const match = editions.find((e) => e.id === initialEditionId)
      if (match) {
        setSelected(match)
        return
      }
    }
    setSelected(editions[0] ?? null)
  }, [body.wikidataId, editions, initialEditionId])

  const currentIdx = selected
    ? editions.findIndex((e) => e.id === selected.id)
    : -1
  const prev = currentIdx > 0 ? editions[currentIdx - 1] : null
  const next =
    currentIdx >= 0 && currentIdx + 1 < editions.length
      ? editions[currentIdx + 1]
      : null

  if (editions.length === 0) {
    return (
      <div className="flex h-full flex-col">
        <BodyDetailHeader body={body} selected={null} onBack={onBack} />
        <div className="flex-1 p-8 text-center text-sm text-text-tertiary">
          No ceremonies on record for this body yet.
        </div>
      </div>
    )
  }

  return (
    <div className="flex h-full flex-col lg:flex-row">
      <CeremonyTimeline
        editions={editions}
        selectedId={selected?.id ?? null}
        onSelect={(ed) => setSelected(ed)}
      />

      <div className="flex-1 overflow-y-auto custom-scrollbar">
        <BodyDetailHeader
          body={body}
          selected={selected}
          onBack={onBack}
          onPrev={prev ? () => setSelected(prev) : undefined}
          onNext={next ? () => setSelected(next) : undefined}
        />

        <div className="mx-auto max-w-7xl px-4 pb-24 pt-6 sm:px-6 lg:px-8">
          {selected ? (
            <CategoryList edition={selected} onOpenDetail={onOpenDetail} />
          ) : (
            <div className="py-20 text-center text-sm text-text-tertiary">
              Select a ceremony from the timeline.
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

function BodyDetailHeader({
  body,
  selected,
  onBack,
  onPrev,
  onNext,
}: {
  body: AwardBodySummary
  selected: RawEdition | null
  onBack: () => void
  onPrev?: () => void
  onNext?: () => void
}) {
  return (
    <div className="relative border-b border-white/5 bg-gradient-to-b from-bg-tertiary/20 to-bg-primary px-4 pt-12 pb-6 sm:px-8">
      <div className="absolute right-4 top-4 z-10">
        <button
          type="button"
          onClick={onBack}
          className="group flex items-center gap-2 rounded-lg border border-white/10 bg-black/40 px-3 py-1.5 text-sm font-medium text-white/80 shadow-lg backdrop-blur-md transition-colors hover:bg-white/10 hover:text-white"
        >
          <svg
            className="h-4 w-4 motion-safe:transition-transform motion-safe:group-hover:-translate-x-0.5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            aria-hidden
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M10 19l-7-7m0 0l7-7m-7 7h18"
            />
          </svg>
          Back
        </button>
      </div>

      <div className="mx-auto max-w-7xl">
        <p className="mb-1 text-xs font-semibold uppercase tracking-[0.2em] text-text-tertiary">
          {body.country ?? 'Film Award'}
        </p>
        <h1 className="text-2xl font-black tracking-tight text-white sm:text-3xl lg:text-4xl">
          {body.name}
        </h1>

        {selected && (
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <span className="rounded-md border border-accent-gold/30 bg-accent-gold/10 px-2.5 py-0.5 text-xs font-bold text-accent-gold">
              {selected.year}
            </span>
            {selected.ordinal && (
              <span className="rounded-md border border-white/10 bg-white/5 px-2.5 py-0.5 text-xs font-semibold text-text-secondary">
                {selected.ordinal}
              </span>
            )}
            <span className="text-xs text-text-tertiary">{selected.name}</span>

            <div className="ml-auto flex items-center gap-1">
              <button
                type="button"
                onClick={onPrev}
                disabled={!onPrev}
                aria-label="Previous ceremony"
                className="rounded-md border border-white/10 bg-white/5 p-1.5 text-text-secondary transition-colors hover:bg-white/10 hover:text-text-primary disabled:cursor-not-allowed disabled:opacity-30"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                  <polyline points="15 18 9 12 15 6" />
                </svg>
              </button>
              <button
                type="button"
                onClick={onNext}
                disabled={!onNext}
                aria-label="Next ceremony"
                className="rounded-md border border-white/10 bg-white/5 p-1.5 text-text-secondary transition-colors hover:bg-white/10 hover:text-text-primary disabled:cursor-not-allowed disabled:opacity-30"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                  <polyline points="9 18 15 12 9 6" />
                </svg>
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
